import { useEffect, useState } from 'react'
import {
  ActionIcon, Alert, Button, CopyButton, Group, Modal, Paper, Select, Stack, Switch, Text, TextInput, Textarea,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconCake, IconCopy, IconCheck, IconTrash, IconPlus } from '@tabler/icons-react'
import type { GreetingTemplateDto, SendGreetingResponse, UpcomingBirthdayDto } from '@ledokol/contracts'
import {
  createGreetingTemplate, deleteGreetingTemplate, fetchGreetingTemplates, sendGreeting, updateGreetingTemplate,
} from '../../shared/api/greetings'

const SEND_REASON: Record<string, string> = {
  contact: 'Контакт — не пользователь бота. Скопируйте текст и отправьте своим каналом.',
  not_started: 'Сотрудник не нажал /start у бота. Скопируйте текст, либо попросите пройти /start.',
  no_tg: 'Нет привязки Telegram. Скопируйте текст.',
}

/** Кнопка «Поздравить» на строке ДР: выбор шаблона → предпросмотр {имя} → отправка/копитекст. */
export function GreetButton({ item }: { item: UpcomingBirthdayDto }) {
  const [opened, setOpened] = useState(false)
  const [templates, setTemplates] = useState<GreetingTemplateDto[]>([])
  const [templateId, setTemplateId] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState<SendGreetingResponse | null>(null)

  useEffect(() => {
    if (!opened) return
    void fetchGreetingTemplates()
      .then((ts) => setTemplates(ts.filter((t) => t.is_active)))
      .catch((e) => notifications.show({ color: 'red', title: 'Ошибка', message: String(e) }))
  }, [opened])

  const tpl = templates.find((t) => t.id === templateId)
  const preview = tpl ? tpl.body.replaceAll('{имя}', item.full_name) : ''

  async function doSend() {
    if (!templateId) return
    setSending(true)
    setResult(null)
    try {
      const r = await sendGreeting({ subject_type: item.subject_type, subject_id: item.subject_id, template_id: templateId })
      setResult(r)
      if (r.delivered) notifications.show({ color: 'green', message: `Отправлено в личку: ${item.full_name}` })
    } catch (e) {
      notifications.show({ color: 'red', title: 'Ошибка', message: String(e) })
    } finally {
      setSending(false)
    }
  }

  return (
    <>
      <Button size="xs" variant="light" leftSection={<IconCake size={14} />} onClick={() => { setOpened(true); setResult(null) }}>
        Поздравить
      </Button>
      <Modal opened={opened} onClose={() => setOpened(false)} title={`Поздравить: ${item.full_name}`} size="lg">
        <Stack>
          <Select
            label="Шаблон"
            placeholder={templates.length ? 'Выберите шаблон' : 'Нет активных шаблонов — заведите в «Шаблоны»'}
            data={templates.map((t) => ({ value: t.id, label: t.name }))}
            value={templateId}
            onChange={setTemplateId}
          />
          {tpl && (
            <Paper withBorder p="sm" radius="md">
              <Text size="xs" c="dimmed" mb={4}>Предпросмотр:</Text>
              <Text style={{ whiteSpace: 'pre-wrap' }}>{preview}</Text>
            </Paper>
          )}
          {result && !result.delivered && (
            <Alert color="orange" variant="light" title="Не доставлено ботом">
              <Stack gap="xs">
                <Text size="sm">{SEND_REASON[result.reason] ?? result.reason}</Text>
                <Textarea value={result.text} readOnly autosize minRows={2} />
                <CopyButton value={result.text}>
                  {({ copied, copy }) => (
                    <Button size="xs" variant="default" leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />} onClick={copy}>
                      {copied ? 'Скопировано' : 'Скопировать текст'}
                    </Button>
                  )}
                </CopyButton>
              </Stack>
            </Alert>
          )}
          {result?.delivered && <Alert color="green" variant="light">Отправлено в личку Telegram.</Alert>}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setOpened(false)}>Закрыть</Button>
            <Button onClick={doSend} loading={sending} disabled={!templateId}>Отправить</Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}

/** Менеджер шаблонов поздравлений (CRUD). */
export function GreetingTemplatesButton() {
  const [opened, setOpened] = useState(false)
  const [templates, setTemplates] = useState<GreetingTemplateDto[]>([])
  const [name, setName] = useState('')
  const [body, setBody] = useState('')
  const [saving, setSaving] = useState(false)

  async function reload() {
    try {
      setTemplates(await fetchGreetingTemplates())
    } catch (e) {
      notifications.show({ color: 'red', title: 'Ошибка', message: String(e) })
    }
  }
  useEffect(() => {
    if (opened) void reload()
  }, [opened])

  async function add() {
    if (!name.trim() || !body.trim()) return
    setSaving(true)
    try {
      await createGreetingTemplate({ name: name.trim(), body: body.trim() })
      setName(''); setBody('')
      await reload()
    } catch (e) {
      notifications.show({ color: 'red', title: 'Ошибка', message: String(e) })
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Button variant="default" size="sm" onClick={() => setOpened(true)}>Шаблоны</Button>
      <Modal opened={opened} onClose={() => setOpened(false)} title="Шаблоны поздравлений" size="lg">
        <Stack>
          <Text size="sm" c="dimmed">{'Используйте {имя} — подставится имя получателя.'}</Text>
          {templates.map((t) => (
            <Paper key={t.id} withBorder p="sm" radius="md">
              <Group justify="space-between" wrap="nowrap">
                <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                  <Group gap="xs">
                    <Text fw={600}>{t.name}</Text>
                    <Switch
                      size="xs"
                      checked={t.is_active}
                      onChange={async (e) => { await updateGreetingTemplate(t.id, { is_active: e.currentTarget.checked }); void reload() }}
                      label={t.is_active ? 'активен' : 'выкл'}
                    />
                  </Group>
                  <Text size="sm" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>{t.body}</Text>
                </Stack>
                <ActionIcon color="red" variant="subtle" onClick={async () => { await deleteGreetingTemplate(t.id); void reload() }}>
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            </Paper>
          ))}
          <Paper withBorder p="sm" radius="md">
            <Stack gap="xs">
              <TextInput label="Название" value={name} onChange={(e) => setName(e.currentTarget.value)} placeholder="С Днём рождения" />
              <Textarea label="Текст" value={body} onChange={(e) => setBody(e.currentTarget.value)} placeholder="Поздравляем, {имя}! ..." autosize minRows={2} />
              <Group justify="flex-end">
                <Button size="xs" leftSection={<IconPlus size={14} />} onClick={add} loading={saving} disabled={!name.trim() || !body.trim()}>
                  Добавить шаблон
                </Button>
              </Group>
            </Stack>
          </Paper>
        </Stack>
      </Modal>
    </>
  )
}
